import { isPostalCode } from './business-csf.type';
import { matchFiscalRegimeCode } from './fiscal-regime.type';
import { RECIPIENT_RFC_RE, normalizeRecipientRfc } from './invoicing-recipient.type';
import { PatientFiscalData } from './patient.type';
import { isCfdiUseCode } from './user-profile.type';

/** The constancia fields that can fill a receptor. Uso de CFDI is not among them. */
export interface CsfFiscalSource {
  rfc?: string;
  regimen?: string;
  codigoPostal?: string;
  satContribuyente?: {
    identificacion?: {
      nombre?: string;
      apellidoPaterno?: string;
      apellidoMaterno?: string;
    };
    domicilioFiscal?: {
      codigoPostal?: string;
    };
  };
}

export function emptyPatientFiscal(): PatientFiscalData {
  return { rfc: '', name: '', fiscalRegime: '', postalCode: '', cfdiUse: '' };
}

/** Keeps only values the factura form can use. Anything else stays blank. */
export function normalizePatientFiscal(value: unknown): PatientFiscalData {
  if (!value || typeof value !== 'object') {
    return emptyPatientFiscal();
  }
  const data = value as Record<string, unknown>;
  const rfc = typeof data['rfc'] === 'string' ? normalizeRecipientRfc(data['rfc']) : '';
  const name = typeof data['name'] === 'string' ? data['name'].replace(/\s+/g, ' ').trim() : '';
  const regime = typeof data['fiscalRegime'] === 'string' ? data['fiscalRegime'].trim() : '';
  const postal = typeof data['postalCode'] === 'string' ? data['postalCode'].replace(/\D/g, '').slice(0, 5) : '';
  return {
    rfc: RECIPIENT_RFC_RE.test(rfc) ? rfc : '',
    name,
    fiscalRegime: /^\d{3}$/.test(regime) ? regime : '',
    postalCode: isPostalCode(postal) ? postal : '',
    cfdiUse: isCfdiUseCode(data['cfdiUse']) ? data['cfdiUse'] : '',
  };
}

export function patientFiscalHasValues(fiscal: PatientFiscalData): boolean {
  return Object.values(fiscal).some((value) => value.trim().length > 0);
}

/** True when the expediente already has everything the receptor step requires. */
export function patientFiscalPresent(fiscal: PatientFiscalData): boolean {
  return (
    RECIPIENT_RFC_RE.test(fiscal.rfc) &&
    fiscal.name.trim().length > 0 &&
    /^\d{3}$/.test(fiscal.fiscalRegime) &&
    isPostalCode(fiscal.postalCode) &&
    isCfdiUseCode(fiscal.cfdiUse)
  );
}

/**
 * Fields a constancia can supply. Uso de CFDI is never on the PDF, so it stays empty.
 * A missing régimen or código postal stays empty too.
 */
export function fiscalFromCsf(result: CsfFiscalSource): PatientFiscalData {
  const identity = result.satContribuyente?.identificacion;
  const name = [identity?.nombre, identity?.apellidoPaterno, identity?.apellidoMaterno]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0)
    .join(' ');
  const rfc = normalizeRecipientRfc(result.rfc || '');
  const regime = RECIPIENT_RFC_RE.test(rfc) ? matchFiscalRegimeCode(result.regimen || '', rfc) : '';
  const postal = result.codigoPostal || result.satContribuyente?.domicilioFiscal?.codigoPostal || '';
  return normalizePatientFiscal({
    rfc,
    name,
    fiscalRegime: regime,
    postalCode: postal,
    cfdiUse: '',
  });
}
